import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectHasFiscalIntegration } from '@store/auth/authSelectors';
import {
  selectAllOutlets,
  selectCanUseCompanyScope,
  selectEffectiveOutletId,
  setSelectedOutletId,
} from '@store/outlets';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  fetchOutletFleetStatus,
  OutletFleetStatus,
} from '@/renderer/services/settingsDashboardService';
import { SettingsStatusTag } from './SettingsStatusTag';
import { DirectoryStatus, getMissingProfileFields } from './summaries';

const ebmCell = (
  status: OutletFleetStatus | undefined,
): { text: string; status: DirectoryStatus } => {
  switch (status?.ebmStatus) {
    case 'ACTIVE':
      return status.ebmHealthOk === false
        ? { text: 'Health check failed', status: 'failing' }
        : { text: 'Active', status: 'ok' };
    case 'FAILED':
      return { text: 'Activation failed', status: 'failing' };
    case 'PENDING_INIT':
      return { text: 'Awaiting activation', status: 'attention' };
    default:
      return { text: 'Not configured', status: 'unset' };
  }
};

const Cell: React.FC<{ text: string; status: DirectoryStatus }> = ({
  text,
  status,
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className={status === 'ok' ? 'text-gray-700' : 'text-gray-500'}>
      {text}
    </span>
    <SettingsStatusTag status={status} />
  </div>
);

/**
 * Compact per-outlet comparison for people who manage several outlets.
 * Selecting a row switches the whole dashboard to that outlet.
 */
export const CompanyOutletStrip: React.FC = () => {
  const dispatch = useAppDispatch();
  const outlets = useAppSelector(selectAllOutlets);
  const canUseCompanyScope = useAppSelector(selectCanUseCompanyScope);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const hasFiscalIntegration = useAppSelector(selectHasFiscalIntegration);

  const outletIds = useMemo(() => outlets.map((o) => o.id).sort(), [outlets]);
  const isVisible = canUseCompanyScope && outlets.length > 1;

  const fleetQuery = useQuery({
    queryKey: queryKeys.settingsDashboard.source('fleet', { outletIds }),
    enabled: isVisible,
    queryFn: () => fetchOutletFleetStatus(outletIds),
  });

  if (!isVisible) return null;

  return (
    <section aria-labelledby="settings-outlets-heading" className="pt-2">
      <div className="mb-2 flex items-center gap-2 px-1">
        <Building2 size={15} className="text-gray-400" aria-hidden />
        <h2
          id="settings-outlets-heading"
          className="text-sm font-semibold text-gray-700"
        >
          All outlets
        </h2>
        <span className="text-sm text-gray-500">
          · select one to see its full setup
        </span>
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="max-h-[32rem] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-xs font-medium text-gray-500">
              <tr>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Outlet
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Receipt profile
                </th>
                {hasFiscalIntegration ? (
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    EBM device
                  </th>
                ) : null}
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Printers
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {outlets.map((outlet) => {
                const fleet = fleetQuery.data?.[outlet.id];
                const missing = getMissingProfileFields(outlet);
                const isCurrent = outlet.id === effectiveOutletId;
                const pending = fleetQuery.isPending ? (
                  <span className="inline-block h-3 w-20 rounded bg-gray-100 motion-safe:animate-pulse" />
                ) : fleetQuery.isError ? (
                  <span className="text-gray-400">Unavailable</span>
                ) : null;
                return (
                  <tr
                    key={outlet.id}
                    className={
                      isCurrent ? 'bg-green-50/40' : 'hover:bg-gray-50'
                    }
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => dispatch(setSelectedOutletId(outlet.id))}
                        aria-current={isCurrent ? 'true' : undefined}
                        className="rounded font-medium text-gray-900 hover:text-pos-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pos-primary"
                      >
                        {outlet.name}
                      </button>
                      {isCurrent ? (
                        <span className="ml-2 text-xs text-pos-primary">
                          Viewing
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <Cell
                        text={
                          missing.length
                            ? `Missing ${missing.length} of 4`
                            : 'Complete'
                        }
                        status={missing.length ? 'attention' : 'ok'}
                      />
                    </td>
                    {hasFiscalIntegration ? (
                      <td className="px-4 py-3">
                        {pending ?? <Cell {...ebmCell(fleet)} />}
                      </td>
                    ) : null}
                    <td className="px-4 py-3 tabular-nums">
                      {pending ?? (
                        <Cell
                          text={
                            fleet?.activePrinters
                              ? `${fleet.activePrinters} active`
                              : 'None active'
                          }
                          status={fleet?.activePrinters ? 'ok' : 'unset'}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
